/**
 * Data layer for the S0 chord-presentation view.
 *
 * Turns the analyzer's chord list + the journey `Phrase` into the model
 * the S0 frieze renders: one entry per chord, **clipped to the phrase
 * window**, carrying the notes actually played during that window.
 *
 * Lives in `components/learn-piece/` rather than in `lib/learning-journey/`
 * because it depends on the analyzer types, and that lib enforces a
 * "no Studio / no Song" rule.
 *
 * ## Why clipping matters
 *
 * `selectPhraseChords` keeps every chord *overlapping* the phrase, which
 * includes lead-in and lead-out chords straddling the boundary. Rendering
 * their raw `durationTicks` would make the frieze lie about where the
 * phrase starts and ends, so every span is clamped and the clamped sides
 * are flagged for the view to mark.
 *
 * ## Why the voicing is derived, not read
 *
 * `AnalysisChord.voicing.inversion` is the analyzer's opinion. On user
 * MIDI it can disagree with the notes actually present in the window
 * (the analyzer looks at a wider context than a single phrase). We
 * re-derive bass and inversion from the concrete notes so the frieze and
 * the keyboard can never contradict each other.
 */

import type {
  AnalysisChord,
  AnalysisHandPlacement,
  AnalysisHandRole,
  AnalysisHandVoicingEntry,
} from "@/lib/music/analyzer-types";
import type { Phrase, PhraseNote } from "@/lib/learning-journey/types";

// ── Chord interval dictionary ─────────────────────────────────────

/**
 * Semitone intervals per analyzer quality string.
 *
 * The analyzer emits a wider vocabulary than `lib/music/chord.ts`'s
 * `ChordQuality` (it adds `dominant7`, `min7`, `mM7`, `power`, ...), so
 * this map is keyed on the raw string and covers the aliases. Unknown
 * qualities resolve to `null`, which switches the view to its silent
 * mode: symbol + concrete notes, no harmonic claim.
 */
const QUALITY_INTERVALS: Readonly<Record<string, readonly number[]>> = {
  major: [0, 4, 7],
  minor: [0, 3, 7],
  m: [0, 3, 7],
  diminished: [0, 3, 6],
  augmented: [0, 4, 8],
  sus2: [0, 2, 7],
  sus4: [0, 5, 7],
  "7": [0, 4, 7, 10],
  dominant7: [0, 4, 7, 10],
  maj7: [0, 4, 7, 11],
  m7: [0, 3, 7, 10],
  min7: [0, 3, 7, 10],
  "6": [0, 4, 7, 9],
  maj6: [0, 4, 7, 9],
  m6: [0, 3, 7, 9],
  min6: [0, 3, 7, 9],
  dim7: [0, 3, 6, 9],
  aug7: [0, 4, 8, 10],
  minmaj7: [0, 3, 7, 11],
  mM7: [0, 3, 7, 11],
  "5": [0, 7],
  power: [0, 7],
};

/** Intervals that act as the chord's third (the note carrying the color). */
/**
 * Semitone offsets of a quality's chord tones, or `null` when the quality is
 * outside the dictionary.
 *
 * Exported so nothing else has to keep a second copy of this table: a panel
 * that decided on its own which keys belong to `Dm` would be free to
 * disagree with the frieze that named it.
 */
export function chordIntervals(quality: string): readonly number[] | null {
  return QUALITY_INTERVALS[quality] ?? null;
}

const THIRD_INTERVALS = new Set([3, 4]);
/** Intervals that act as the chord's fifth. */
const FIFTH_INTERVALS = new Set([6, 7, 8]);

// ── Public model ──────────────────────────────────────────────────

/** Notes of one hand sounding during a chord window. */
export type PhraseChordHand = {
  /** Notes *struck* inside the window. Deduped, ascending. */
  readonly midis: readonly number[];
  /**
   * Notes started before the window but still held through it. Rendered
   * dimmed: the player keeps the finger down instead of re-attacking.
   */
  readonly sustainedMidis: readonly number[];
  /**
   * The struck notes with their onsets, in time order. `midis` answers
   * "which keys"; this answers "in what order and how spread out".
   */
  readonly notes: readonly PhraseNote[];
  /**
   * What this hand does over the window, from the analyzer: `harmonic` when
   * it states or prolongs the chord, `melodic` when it conducts a line
   * through it. A question of texture — it does not say whether the hand can
   * be drawn as lit keys, `placement` does. `null` when the analyzer had no
   * opinion, or on payloads predating the field.
   */
  readonly role: AnalysisHandRole | null;
  /**
   * Whether the keys this hand is on during the window fit under one hand,
   * decided by the analyzer. This is what `chordDrawing` reads. `null` on
   * analyses stored before the field existed — see `handHasPosition`.
   */
  readonly placement: AnalysisHandPlacement | null;
};

/** One chord of the phrase, clipped to the phrase window. */
export type PhraseChord = {
  readonly chord: AnalysisChord;
  /** Window start, clamped to the phrase. */
  readonly startTick: number;
  /** Window end, clamped to the phrase. */
  readonly endTick: number;
  /** `endTick - startTick`, always > 0. */
  readonly spanTicks: number;
  /** The analyzer chord began before the phrase. */
  readonly clippedStart: boolean;
  /** The analyzer chord runs past the phrase. */
  readonly clippedEnd: boolean;
  readonly left: PhraseChordHand;
  readonly right: PhraseChordHand;
  /** Every midi sounding in the window (struck + sustained), ascending. */
  readonly midis: readonly number[];
  /** Lowest sounding midi, or `null` when the window is silent. */
  readonly bassMidi: number | null;
  /**
   * Derived from the concrete notes: `0` = root position, `1` = 1st
   * inversion, and so on. `0` when the quality is unknown or the window
   * is silent.
   */
  readonly inversion: number;
  /** True when the chord has a third in theory but none is played. */
  readonly missingThird: boolean;
  /** True when the chord has a fifth in theory but none is played. */
  readonly missingFifth: boolean;
  /** False when `quality` is outside `QUALITY_INTERVALS`. */
  readonly qualityKnown: boolean;
};

/**
 * Ce que l'analyseur a écrit dans cette fenêtre, touche par touche.
 *
 * Construit ici parce que c'est le dernier endroit où les notes sont encore
 * entières : plus bas, le rythme ne garde d'elles que leur numéro de touche.
 * Une carte par fenêtre suffit, et non par note : deux notes de même touche
 * dans une même fenêtre d'accord portent la même lettre, faute de quoi ce
 * serait l'analyse qui se contredirait.
 *
 * Vide quand rien n'est épelé — une analyse antérieure au champ — et le dessin
 * retombe alors sur sa propre déduction, qui est la même règle en moins bien
 * informée. Voir `windowToneName`.
 */
export function spellingOf(entry: PhraseChord): ReadonlyMap<number, string> | null {
  const spelling = new Map<number, string>();

  for (const note of [...entry.left.notes, ...entry.right.notes]) {
    if (note.spelling != null && !spelling.has(note.midi)) {
      spelling.set(note.midi, note.spelling);
    }
  }

  return spelling.size === 0 ? null : spelling;
}


// ── Selection ─────────────────────────────────────────────────────

/**
 * Returns the chords that overlap `[startTick, endTick)`. A chord
 * overlaps the phrase when its `[tick, endTick)` intersects the phrase
 * window, so fully-contained chords, lead-in chords and lead-out chords
 * all qualify. The result preserves the analyzer ordering.
 */
export function selectPhraseChords(
  chords: readonly AnalysisChord[],
  startTick: number,
  endTick: number,
): AnalysisChord[] {
  const out: AnalysisChord[] = [];

  for (const chord of chords) {
    const overlaps = chord.tick < endTick && chord.endTick > startTick;

    if (overlaps) {
      out.push(chord);
    }
  }

  return out;
}

// ── Build ─────────────────────────────────────────────────────────

/**
 * Build the S0 model for a phrase.
 *
 * Chords whose clipped span collapses to zero are dropped: they touch
 * the phrase boundary without occupying any of it, and a zero-width
 * frieze cell would be meaningless.
 *
 * Only the bounds and the notes are read, so a span that is not a phrase
 * goes through as well: the harmonic journey cuts its windows over the
 * whole song. See `buildSongSpan`.
 */
export function buildPhraseChords(
  chords: readonly AnalysisChord[],
  phrase: Pick<Phrase, "startTick" | "endTick" | "notesByHand">,
): PhraseChord[] {
  const selected = selectPhraseChords(
    chords,
    phrase.startTick,
    phrase.endTick,
  );
  const out: PhraseChord[] = [];

  for (const chord of selected) {
    const startTick = Math.max(chord.tick, phrase.startTick);
    const endTick = Math.min(chord.endTick, phrase.endTick);

    if (endTick <= startTick) {
      continue;
    }

    const left = handNotesInWindow(
      phrase.notesByHand.left,
      startTick,
      endTick,
      chord.handVoicing?.left ?? null,
    );
    const right = handNotesInWindow(
      phrase.notesByHand.right,
      startTick,
      endTick,
      chord.handVoicing?.right ?? null,
    );

    const midis = sortedUnique([
      ...left.midis,
      ...left.sustainedMidis,
      ...right.midis,
      ...right.sustainedMidis,
    ]);
    const bassMidi = midis.length > 0 ? midis[0] : null;
    const intervals = QUALITY_INTERVALS[chord.quality] ?? null;

    out.push({
      chord,
      startTick,
      endTick,
      spanTicks: endTick - startTick,
      clippedStart: chord.tick < phrase.startTick,
      clippedEnd: chord.endTick > phrase.endTick,
      left,
      right,
      midis,
      bassMidi,
      inversion: deriveInversion(chord.rootPitchClass, bassMidi, intervals),
      missingThird: isDegreeMissing(
        chord.rootPitchClass,
        midis,
        intervals,
        THIRD_INTERVALS,
      ),
      missingFifth: isDegreeMissing(
        chord.rootPitchClass,
        midis,
        intervals,
        FIFTH_INTERVALS,
      ),
      qualityKnown: intervals !== null,
    });
  }

  return out;
}

// ── Keyboard window ───────────────────────────────────────────────

/** A `MiniPiano` window: lowest C shown plus a count of octaves. */
export type KeyboardWindow = {
  readonly lowMidi: number;
  readonly octaves: number;
};

const DEFAULT_WINDOW: KeyboardWindow = { lowMidi: 48, octaves: 2 };
const MAX_OCTAVES = 4;

/**
 * One window shared by every chord of the phrase.
 *
 * This is the load-bearing detail of the frieze: if each cell picked its
 * own octave range, the little keyboards would no longer be comparable
 * and the "shape of the hand moving" effect — the whole point — would
 * collapse into four unrelated pictures.
 *
 * The range is snapped outward to whole octaves (`MiniPiano` requires a
 * C-aligned `lowMidi`) and capped, so a phrase spanning the full keyboard
 * degrades to a wide-but-readable window instead of an unusable sliver.
 */
export function phraseKeyboardWindow(
  phraseChords: readonly PhraseChord[],
): KeyboardWindow {
  const all: number[] = [];

  for (const entry of phraseChords) {
    all.push(...entry.midis);
  }

  if (all.length === 0) {
    return DEFAULT_WINDOW;
  }

  const lowMidi = Math.floor(Math.min(...all) / 12) * 12;
  const highMidi = Math.max(...all);
  const octaves = Math.min(
    MAX_OCTAVES,
    Math.max(1, Math.ceil((highMidi - lowMidi + 1) / 12)),
  );

  return { lowMidi, octaves };
}

/** How one chord window should be drawn, hand by hand. */
export type ChordDrawing = {
  /**
   * Midis that may be lit as keys: the hands that have a placement. Lighting
   * these together asserts a position, and for these hands that assertion is
   * true.
   */
  readonly positionMidis: readonly number[];
  /** Hands whose notes must be drawn as a rail instead of as keys. */
  readonly trajectories: readonly {
    readonly hand: "left" | "right";
    readonly notes: readonly PhraseNote[];
  }[];
};

/**
 * Split a chord window into what can be shown as a hand position and what
 * cannot.
 *
 * The question the drawing asks is physical: is there a hand position to
 * take here? `placement.holdable` is the analyzer's decided answer to exactly
 * that question, so it is the criterion. A hand whose keys fit under five
 * fingers keeps them lit, because "pose tes doigts là" is then true of it; a
 * hand that travels gets the rail, because lighting its keys would assert a
 * shape that existed at no single instant. Nothing is dropped either way — a
 * hand changes grammar, it never disappears.
 *
 * This used to key on `role`, which answers a different question: does the
 * hand state the harmony? A wide arpeggio answers yes to that and no to this
 * one, and on the corpus the two disagree on about a third of the hands in
 * each direction. `role` survives here only as the fallback for analyses
 * predating `placement` — see `handHasPosition`.
 */
export function chordDrawing(entry: PhraseChord): ChordDrawing {
  const positionMidis: number[] = [];
  const trajectories: {
    hand: "left" | "right";
    notes: readonly PhraseNote[];
  }[] = [];

  for (const hand of ["left", "right"] as const) {
    const side = entry[hand];

    if (side.midis.length === 0 && side.sustainedMidis.length === 0) {
      continue;
    }

    // Travelling hands get the rail — but a rail needs onsets to run
    // through. A hand that struck nothing inside the window would draw an
    // empty one, which reads as "this hand is silent"; its keys are held
    // down from before, so they stay lit instead — dimmed, the "note tenue"
    // grammar rather than a claim of a new position.
    if (!handHasPosition(side) && side.notes.length > 0) {
      trajectories.push({ hand, notes: side.notes });

      continue;
    }

    positionMidis.push(...side.midis, ...side.sustainedMidis);
  }

  return { positionMidis: sortedUnique(positionMidis), trajectories };
}

/**
 * Narrow a window to at most `maxOctaves`, centred on the register actually
 * played, snapped to a C.
 *
 * The frieze draws its keyboards in ~110px cells. At four octaves a white
 * key is under 4px wide — a smear, not a keyboard — so the frieze trades
 * the full range for a readable one while the focus block keeps the
 * complete window. What must not be traded is that every frieze cell keeps
 * the *same* window: comparability between cells is the whole point, the
 * shared range with the focus block is only a nicety.
 */
export function clampKeyboardWindow(
  window: KeyboardWindow,
  maxOctaves: number,
  midis: readonly number[],
): KeyboardWindow {
  if (window.octaves <= maxOctaves || midis.length === 0) {
    return window;
  }

  const sorted = [...midis].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  const span = maxOctaves * 12;

  // Centre on the median, then snap down to a C and keep the result inside
  // the original window so we never show keys the phrase cannot reach.
  const rawLow = median - Math.floor(span / 2);
  const maxLow = window.lowMidi + (window.octaves - maxOctaves) * 12;
  const lowMidi = Math.min(
    Math.max(Math.floor(rawLow / 12) * 12, window.lowMidi),
    maxLow,
  );

  return { lowMidi, octaves: maxOctaves };
}

// ── Internals ─────────────────────────────────────────────────────

/**
 * Split a hand's notes against a window into struck vs. sustained.
 *
 * A note counts as *struck* when its onset falls inside the window, and
 * as *sustained* when it started earlier but is still sounding. The
 * distinction drives both the dimmed keys on the keyboard and the
 * "note tenue" explanation — a motor skill the chord symbol hides.
 *
 * The analyzer's per-hand answers are carried through as-is: both are
 * decisions taken upstream, and re-deriving either here would be this module
 * second-guessing an analysis it cannot redo.
 */
function handNotesInWindow(
  notes: readonly PhraseNote[],
  startTick: number,
  endTick: number,
  voicing: AnalysisHandVoicingEntry | null,
): PhraseChordHand {
  const struck: PhraseNote[] = [];
  const sustained: number[] = [];

  for (const note of notes) {
    const sounds = note.startTick < endTick && note.endTick > startTick;

    if (!sounds) {
      continue;
    }

    if (note.startTick >= startTick) {
      struck.push(note);

      continue;
    }

    sustained.push(note.midi);
  }

  const struckMidis = struck.map((n) => n.midi);
  const struckSet = new Set(struckMidis);

  return {
    midis: sortedUnique(struckMidis),
    // A midi re-struck inside the window is not "held": drop it from the
    // sustained set so it never renders dimmed and lit at once.
    sustainedMidis: sortedUnique(
      sustained.filter((midi) => !struckSet.has(midi)),
    ),
    notes: [...struck].sort((a, b) => a.startTick - b.startTick),
    role: voicing?.role ?? null,
    placement: voicing?.placement ?? null,
  };
}

/**
 * Does this hand have a position to hold over the window?
 *
 * `placement` is a decision taken by the analyzer and is applied as-is. It is
 * absent only on analyses stored before the field existed, and those degrade
 * to the rule they were rendered with: `melodic` travels, everything else
 * keeps its keys — including `undetermined` and a missing role, because
 * hiding a hand on "no opinion" would be worse than showing it.
 *
 * That fallback is **transitional**. It exists for the analyses already in
 * the database and can go once none of them predates `placement`; until then
 * it is what keeps an old piece drawing something rather than nothing.
 */
function handHasPosition(side: PhraseChordHand): boolean {
  if (side.placement !== null) {
    return side.placement.holdable;
  }

  return side.role !== "melodic";
}

/**
 * Inversion from the concrete bass: the index of the chord tone sitting
 * in the bass. Returns `0` (root position) when the bass is not a chord
 * tone, which happens on passing notes and on analyzer disagreements —
 * claiming an inversion we cannot justify would be worse than silence.
 */
function deriveInversion(
  rootPitchClass: number,
  bassMidi: number | null,
  intervals: readonly number[] | null,
): number {
  if (bassMidi === null || intervals === null) {
    return 0;
  }

  const bassPc = ((bassMidi % 12) + 12) % 12;

  for (let i = 0; i < intervals.length; i++) {
    if ((rootPitchClass + intervals[i]) % 12 === bassPc) {
      return i;
    }
  }

  return 0;
}

/**
 * True when the quality defines a degree in `degreeIntervals` and none of
 * the played pitch classes covers it. Unknown qualities and chords with
 * no such degree (a power chord has no third) answer `false`: nothing is
 * "missing" from a shape that never had it.
 */
function isDegreeMissing(
  rootPitchClass: number,
  midis: readonly number[],
  intervals: readonly number[] | null,
  degreeIntervals: ReadonlySet<number>,
): boolean {
  if (intervals === null || midis.length === 0) {
    return false;
  }

  const expected = intervals.filter((i) => degreeIntervals.has(i));

  if (expected.length === 0) {
    return false;
  }

  const playedPcs = new Set(midis.map((m) => ((m % 12) + 12) % 12));

  return !expected.some((i) => playedPcs.has((rootPitchClass + i) % 12));
}

function sortedUnique(values: readonly number[]): number[] {
  return [...new Set(values)].sort((a, b) => a - b);
}
